import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createApiKey,
  deleteApiKey,
  errorText,
  fetchApiKeys,
  type ApiKey,
  type NewApiKey,
} from './controlApi';
import { ApiError, fetchSites, type Site } from './fleetApi';
import { ConfirmDialog } from './shared/ConfirmDialog';
import { PageHeader } from './shared/PageHeader';

/** Plain-language names for permissions a key can carry. */
const PERMISSION_LABELS: Record<string, string> = {
  'tenant.read': 'Read the organization',
  'site.read': 'Read sites and lighting state',
  'appliance.read': 'Read appliances and their status',
  'action.execute': 'Control lighting (presets, scenes, groups)',
  'schedule.write': 'Edit schedules',
  'appliance.claim': 'Add appliances',
  'endpoint.write': 'Configure lighting endpoints',
  'config.deploy': 'Deploy configurations',
  'alert.ack': 'Acknowledge alerts',
  'alert_rule.write': 'Edit alert rules and notification channels',
  'audit.read': 'Read the audit log',
};

/** Permissions a key gets unless the user changes them. */
const DEFAULT_SCOPES = ['site.read', 'appliance.read', 'action.execute'];

const EXPIRY_DAYS: Record<string, number | null> = { never: null, '30': 30, '90': 90, '365': 365 };

interface ApiKeysProps {
  apiUrl: string;
  tenantId: string;
  token: string;
  /** The user's permissions in the tenant. */
  permissions: string[];
  /** The user's own site restriction (undefined = all sites). */
  siteIds?: string[];
  userId: string;
  /** Sign in again (API keys need a recent sign-in). */
  onReauthenticate: () => void;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; keys: ApiKey[]; sites: Site[] }
  | { kind: 'error'; error: string };

/** Create, list, and delete API keys for external automation (IAM-008). */
export function ApiKeys({
  apiUrl,
  tenantId,
  token,
  permissions,
  siteIds,
  userId,
  onReauthenticate,
}: ApiKeysProps) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ApiKey | null>(null);
  const [deleting, setDeleting] = useState<ApiKey | null>(null);
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const canCreate = permissions.includes('api_key.create');
  const isAdmin = permissions.includes('api_key.admin');

  const reload = useCallback(
    (signal?: AbortSignal) => {
      Promise.all([
        fetchApiKeys(apiUrl, tokenRef.current, tenantId, signal),
        fetchSites(apiUrl, tokenRef.current, tenantId, signal),
      ])
        .then(([keys, sites]) => {
          setLoad({ kind: 'ready', keys, sites });
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          setLoad({ kind: 'error', error: errorText(err) });
        });
    },
    [apiUrl, tenantId],
  );

  useEffect(() => {
    if (!canCreate) return;
    const controller = new AbortController();
    reload(controller.signal);
    return () => {
      controller.abort();
    };
  }, [reload, canCreate]);

  if (!canCreate) {
    return (
      <Alert severity="info" data-testid="api-keys-not-allowed">
        Your role doesn&apos;t include creating API keys. Ask a technician or administrator.
      </Alert>
    );
  }
  if (load.kind === 'loading')
    return <Typography color="text.secondary">Loading API keys…</Typography>;
  if (load.kind === 'error')
    return <Alert severity="error">Could not load API keys: {load.error}</Alert>;

  const siteName = (id: string) => load.sites.find((s) => s.id === id)?.name ?? id;

  return (
    <Stack spacing={2} data-testid="api-keys">
      <PageHeader
        title="API keys"
        subtitle="For building management systems, AV control, and scripts. A key can do no more than you can, and stops working if your access is removed."
        actions={
          <Button
            variant="contained"
            data-testid="create-key-open"
            onClick={() => {
              setCreating(true);
            }}
          >
            Create key
          </Button>
        }
      />

      {load.keys.length === 0 && <Alert severity="info">No API keys yet.</Alert>}
      {load.keys.map((k) => (
        <Card key={k.key_id} variant="outlined" data-testid={`api-key-${k.key_id}`}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{k.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {k.key_id}
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                variant="outlined"
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
                data-testid={`delete-key-${k.key_id}`}
                onClick={() => {
                  setDeleting(k);
                }}
              >
                Delete
              </Button>
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, my: 1 }}>
              {k.scopes.map((s) => (
                <Chip key={s} size="small" label={PERMISSION_LABELS[s] ?? s} />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {k.site_ids ? `Sites: ${k.site_ids.map(siteName).join(', ')}` : 'All your sites'}
              {' · '}
              Created {new Date(k.created_at).toLocaleDateString()}
              {' · '}
              {k.expires_at
                ? `Expires ${new Date(k.expires_at).toLocaleDateString()}`
                : 'Never expires'}
              {' · '}
              {k.last_used_at
                ? `Last used ${new Date(k.last_used_at).toLocaleString()}${k.last_used_ip ? ` from ${k.last_used_ip}` : ''}`
                : 'Never used'}
            </Typography>
            {isAdmin && k.owner && k.owner.user_id !== userId && (
              <Typography variant="body2" color="text.secondary">
                Created by {k.owner.email ?? k.owner.user_id}
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}

      <CreateKeyDialog
        open={creating}
        permissions={permissions}
        sites={load.sites}
        siteIds={siteIds}
        onClose={() => {
          setCreating(false);
        }}
        onReauthenticate={onReauthenticate}
        onCreate={async (req) => {
          const key = await createApiKey(apiUrl, tokenRef.current, tenantId, req);
          setCreated(key);
          setLoad((prev) =>
            prev.kind === 'ready'
              ? { ...prev, keys: [{ ...key, key: undefined }, ...prev.keys] }
              : prev,
          );
        }}
      />

      <ShowKeyDialog
        apiKey={created}
        onClose={() => {
          setCreated(null); // the secret is gone from the page for good
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.name ?? ''}?`}
        confirmLabel="Delete"
        confirmTestId="confirm-delete-key"
        onClose={() => {
          setDeleting(null);
        }}
        onConfirm={async () => {
          const k = deleting;
          if (!k) return;
          await deleteApiKey(apiUrl, tokenRef.current, tenantId, k.key_id);
          setLoad((prev) =>
            prev.kind === 'ready'
              ? { ...prev, keys: prev.keys.filter((x) => x.key_id !== k.key_id) }
              : prev,
          );
        }}
      >
        Anything using this key stops working immediately. This can&apos;t be undone.
      </ConfirmDialog>
    </Stack>
  );
}

function CreateKeyDialog({
  open,
  permissions,
  sites,
  siteIds,
  onClose,
  onCreate,
  onReauthenticate,
}: {
  open: boolean;
  permissions: string[];
  sites: Site[];
  siteIds?: string[];
  onClose: () => void;
  onCreate: (req: NewApiKey) => Promise<void>;
  onReauthenticate: () => void;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const grantable = permissions.filter((p) => p in PERMISSION_LABELS);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(() =>
    DEFAULT_SCOPES.filter((s) => grantable.includes(s)),
  );
  const [expiry, setExpiry] = useState('never');
  const [restrict, setRestrict] = useState<string[]>(siteIds ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; reauth: boolean } | null>(null);
  const available = siteIds ? sites.filter((s) => siteIds.includes(s.id)) : sites;

  const submit = () => {
    const req: NewApiKey = { name: name.trim(), scopes };
    const days = EXPIRY_DAYS[expiry];
    if (days) req.expires_at = new Date(Date.now() + days * 86_400_000).toISOString();
    if (restrict.length > 0) req.site_ids = restrict;
    setBusy(true);
    setError(null);
    onCreate(req)
      .then(() => {
        setName('');
        onClose();
      })
      .catch((err: unknown) => {
        setError({
          text: errorText(err),
          reauth: err instanceof ApiError && err.code === 'reauthentication_required',
        });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <DialogTitle>Create an API key</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && (
            <Alert
              severity={error.reauth ? 'info' : 'error'}
              action={
                error.reauth ? (
                  <Button
                    color="inherit"
                    size="small"
                    data-testid="reauthenticate"
                    onClick={onReauthenticate}
                  >
                    Sign in
                  </Button>
                ) : undefined
              }
            >
              {error.reauth ? 'For security, sign in again to create a key.' : error.text}
            </Alert>
          )}
          <TextField
            label="Name"
            value={name}
            placeholder="e.g. Building management system"
            slotProps={{ htmlInput: { 'data-testid': 'key-name', maxLength: 100 } }}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <FormControl component="fieldset">
            <FormLabel component="legend">What can it do?</FormLabel>
            <FormGroup>
              {grantable.map((p) => (
                <FormControlLabel
                  key={p}
                  label={PERMISSION_LABELS[p]}
                  control={
                    <Checkbox
                      checked={scopes.includes(p)}
                      onChange={(e) => {
                        setScopes((prev) =>
                          e.target.checked ? [...prev, p] : prev.filter((x) => x !== p),
                        );
                      }}
                    />
                  }
                />
              ))}
            </FormGroup>
          </FormControl>
          <FormControl>
            <InputLabel id="key-expiry-label">Expires</InputLabel>
            <Select
              labelId="key-expiry-label"
              label="Expires"
              value={expiry}
              onChange={(e) => {
                setExpiry(e.target.value);
              }}
            >
              <MenuItem value="never">Never</MenuItem>
              <MenuItem value="30">In 30 days</MenuItem>
              <MenuItem value="90">In 90 days</MenuItem>
              <MenuItem value="365">In a year</MenuItem>
            </Select>
          </FormControl>
          {available.length > 1 && (
            <FormControl>
              <InputLabel id="key-sites-label">Sites</InputLabel>
              <Select
                labelId="key-sites-label"
                label="Sites"
                multiple
                value={restrict}
                renderValue={(ids) =>
                  ids.length === 0
                    ? 'All your sites'
                    : ids.map((id) => sites.find((s) => s.id === id)?.name).join(', ')
                }
                displayEmpty
                onChange={(e) => {
                  const v = e.target.value;
                  setRestrict(typeof v === 'string' ? v.split(',') : v);
                }}
              >
                {available.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox checked={restrict.includes(s.id)} />
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          data-testid="key-submit"
          disabled={busy || !name.trim() || scopes.length === 0}
          onClick={submit}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ShowKeyDialog({ apiKey, onClose }: { apiKey: ApiKey | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const value = apiKey?.key ?? '';
  return (
    <Dialog
      open={apiKey !== null}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') onClose(); // don't lose the key to a stray tap
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Copy your new API key</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">
            This is the only time the key is shown. Store it in your automation system&apos;s secret
            store now.
          </Alert>
          <TextField
            value={value}
            label={apiKey?.name}
            slotProps={{
              htmlInput: { readOnly: true, 'data-testid': 'new-key', spellCheck: false },
              input: { sx: { fontFamily: 'monospace', fontSize: 14 } },
            }}
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Send it as <code>Authorization: Bearer &lt;key&gt;</code> to the IllumiLink REST API.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<ContentCopyIcon />}
          data-testid="copy-key"
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          variant="contained"
          data-testid="key-done"
          onClick={() => {
            setCopied(false);
            onClose();
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
